import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';
import { ApiError, authClient } from '@/shared/api';
import { paths } from '@/shared/lib/paths';
import { Form } from '@/shared/ui/form';
import { applyFieldErrors } from '@/shared/ui/form/apply-field-errors';
import { Button } from '@/shared/ui/primitives/button';
import { authErrorMessage } from '../components/auth-errors';
import {
  type ResetPasswordFormValues,
  resetPasswordFormSchema,
} from '../components/auth-form-schemas';
import { AuthPageLayout } from '../components/auth-page-layout';
import { FormAlert } from '../components/form-alert';
import { TextField } from '../components/text-field';
import { tokenFromHash } from './token-from-hash';

const INVALID_INVITATION = 'USER_INVITATION_INVALID';

function InvalidInvitation(): ReactNode {
  return (
    <AuthPageLayout title="Convite inválido">
      <p role="alert">
        Este convite é inválido, venceu ou já foi usado. Peça um convite novo a quem convidou você.
        Se você já criou a sua senha, é só entrar.
      </p>
      <Button asChild className="w-full">
        <Link to={paths.login()}>Entrar</Link>
      </Button>
    </AuthPageLayout>
  );
}

/**
 * USR-2: o convidado define a própria senha com o token do convite. O token chega no
 * fragmento da URL (não vai ao servidor nem ao Referer) e sai da barra de endereço assim que é
 * lido. Ao terminar, a pessoa entra pelo login.
 */
export function AcceptInvitationPage(): ReactNode {
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState(() => tokenFromHash(location.hash));
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: { newPassword: '', confirmation: '' },
  });

  useEffect(() => {
    if (location.hash !== '') void navigate(location.pathname, { replace: true });
  }, [location.hash, location.pathname, navigate]);

  async function submit(values: ResetPasswordFormValues): Promise<void> {
    if (token === null) return;
    setFailure(null);
    try {
      await authClient.acceptInvitation({ token, newPassword: values.newPassword });
      setDone(true);
    } catch (error) {
      if (error instanceof ApiError && error.code === INVALID_INVITATION) {
        setInvalid(true);
        return;
      }
      if (!applyFieldErrors(error, form.setError, ['newPassword'])) {
        setFailure(authErrorMessage(error, 'Não foi possível criar a senha. Tente de novo.'));
      }
    }
  }

  if (token === null || invalid) return <InvalidInvitation />;
  if (done) {
    return (
      <AuthPageLayout title="Senha criada">
        <p role="status">Pronto! Entre com o seu e-mail e a senha que você acabou de criar.</p>
        <Button asChild className="w-full">
          <Link to={paths.login()}>Entrar</Link>
        </Button>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      title="Boas-vindas ao JAF"
      description="Crie a sua senha para aceitar o convite. Use de 12 a 128 caracteres: uma frase longa é fácil de lembrar e difícil de adivinhar."
    >
      <Form {...form}>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(event) => {
            void form.handleSubmit(submit)(event);
          }}
        >
          {failure !== null && <FormAlert>{failure}</FormAlert>}
          <TextField
            control={form.control}
            name="newPassword"
            label="Senha"
            type="password"
            autoComplete="new-password"
          />
          <TextField
            control={form.control}
            name="confirmation"
            label="Confirme a senha"
            type="password"
            autoComplete="new-password"
          />
          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            Criar senha e aceitar convite
          </Button>
        </form>
      </Form>
    </AuthPageLayout>
  );
}
